/**
 * Tiny undo-toast context. Any component can call `show(message, onUndo)`
 * after a destructive-feeling action; the snackbar mounted at the layout
 * root surfaces the message with an Undo button for a short window.
 *
 * One toast at a time. A new `show` replaces the previous one — the most
 * recent action is always the one a user would want to revert.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';

const UNDO_WINDOW_MS = 6000;

interface UndoState {
  id: number;
  message: string;
  onUndo: () => void;
}

interface UndoContextValue {
  current: UndoState | null;
  show: (message: string, onUndo: () => void) => void;
  dismiss: () => void;
}

const UndoContext = createContext<UndoContextValue | null>(null);

export function UndoProvider({ children }: { children: ReactNode }) {
  const [current, setCurrent] = useState<UndoState | null>(null);
  const counter = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const dismiss = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setCurrent(null);
  }, []);

  const show = useCallback((message: string, onUndo: () => void) => {
    counter.current += 1;
    const id = counter.current;
    if (timerRef.current) clearTimeout(timerRef.current);
    setCurrent({ id, message, onUndo });
    timerRef.current = setTimeout(() => {
      setCurrent((prev) => (prev && prev.id === id ? null : prev));
      timerRef.current = null;
    }, UNDO_WINDOW_MS);
  }, []);

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    [],
  );

  return (
    <UndoContext.Provider value={{ current, show, dismiss }}>{children}</UndoContext.Provider>
  );
}

export function useUndoToast() {
  const ctx = useContext(UndoContext);
  if (!ctx) throw new Error('useUndoToast must be used inside <UndoProvider>');
  return ctx;
}
